import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Field } from '../../../core/store/models';
import { MatChipInputEvent } from '@angular/material/chips';

interface PDropdownOption {
    id: string;
    name: any;
}
@Component({
    selector: 'ah-multi-select-input',
    templateUrl: './multi-select-input.component.html',
    styleUrls: ['./multi-select-input.component.scss'],
    standalone: false
})
export class MultiSelectInputComponent implements OnInit, OnChanges {
    @Input() allOptions: PDropdownOption[];
    @Input() disabled = false;
    @Input() controlName;
    @Input() selectedOptions?: string[] = [];
    @Input() field: Field;
    @Output() updateFieldEvent = new EventEmitter<any>();
    @ViewChild('optionInput', { static: true }) optionInput: ElementRef;
    @ViewChild(MatAutocompleteTrigger, { static: true }) trigger: MatAutocompleteTrigger;

    separatorKeysCodes: number[] = [ENTER, COMMA];
    groupForm: UntypedFormGroup;
    options: PDropdownOption[];
    filteredStudioOptions$: Observable<PDropdownOption[]>;
    selectedOptionId: number[] = [];

    constructor(private fb: UntypedFormBuilder) { }

    ngOnInit(): void {
        this.groupForm = this.fb.group({
            [this.controlName]: '',
        });
        this.setOptions();
        this.filteredStudioOptions$ = this.groupForm.get(this.controlName)!.valueChanges
            .pipe(
                startWith<string | PDropdownOption>(''),
                map(value => typeof value === 'string' ? value : value.name),
                map(value => value ? this.filterOptions(value) : this.options.slice())
            );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.allOptions) {
            this.setOptions();
        }
        if (changes.disabled) {
            const control = this.groupForm.controls[this.controlName];
            if (changes.disabled.currentValue) {
                control.disable();
            } else if (control.disabled) {
                control.enable();
            }
        }
    }

    setOptions() {
        this.options = [...this.setOptionsValue(this.allOptions)];
        this.filteredStudioOptions$ = of(this.options);
        this.selectedOptionId = [];
    }

    setOptionsValue(options: any[]) {
        let modifiedOptions = [];
        if (options && options.length) {
            options.forEach((option) => {
                modifiedOptions.push({ id: option.value, name: option.label });
            });
        }
        const allOptions = modifiedOptions.filter(option => option.id !== null);
        return allOptions;
    }

    filterOptions(name: string): PDropdownOption[] {
        const filterValue = name.toLowerCase();
        return this.options.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    addOption(event: MatChipInputEvent) {
        const input = event.input;
        const value = event.value.trim();
        const selectedOption = this.options.find(option => option.name === value);
        if (selectedOption) {
            this.addOptionToSelected(selectedOption);
        }
        if (input) {
            input.value = '';
        }
        this.clearOptionFilters();
    }

    addOptionToSelected(option: PDropdownOption) {
        if (this.selectedOptions.indexOf(option.name) < 0) {
            this.selectedOptions.push(option.name);
            this.selectedOptionId.push(+option.id);
        }
    }

    clearOptionFilters() {
        this.groupForm.patchValue({ [this.controlName]: '' });
        setTimeout(() => {
            this.trigger.openPanel();
        });
    }

    showAutoComplete() {
        return !(this.allOptions.length === 0);
    }

    selectOption(event: MatAutocompleteSelectedEvent) {
        let optionName = event.option.viewValue;
        const selectedOption = this.options.find(option => option.name === optionName);
        this.addOptionToSelected(selectedOption);
        this.optionInput.nativeElement.value = '';
        this.clearOptionFilters();
        this.updateFieldEvent.emit(this.selectedOptionId);
    }

    removeOption(option: string) {
        const optionObj = this.options.find(o => o.name === option);
        const index = this.selectedOptions.indexOf(option);
        if (index >= 0) {
            this.selectedOptions.splice(index, 1);
            this.selectedOptionId = this.selectedOptionId.filter(id => id !== +optionObj.id);
        }
        this.updateFieldEvent.emit(this.selectedOptionId);
    }
}